import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Popover } from "antd";
import { ChevronDown, ChevronRight, Filter } from "lucide-react";
import {
  BriefDeliverableStatusEnum,
  UploaderType,
} from "@entities/brief/lib/constants";
import { IBriefDeliverableConfig } from "@shared/lib/types/briefConfig";
import { IBriefCampaignInfluencerSearchRequestV2 } from "@shared/lib/types/briefCampains";
import { SearchMultiSelectFilter } from "@shared/ui/filters/SearchMultiSelectFilter";
import styles from "./CampaignExecutionDeliverableTabs.module.scss";

export interface CampaignExecutionDeliverableTabsProps {
  config?: IBriefDeliverableConfig[];
  selectedDeliverableId: string | null;
  onSelectDeliverable: (deliverableId: string | null) => void;
  filters?: IBriefCampaignInfluencerSearchRequestV2;
  setFilters?: React.Dispatch<
    React.SetStateAction<IBriefCampaignInfluencerSearchRequestV2>
  >;
  isAllExpanded?: boolean;
  onToggleExpandAll?: () => void;
}

const DELIVERABLE_STATUS_OPTIONS = [
  ...Object.values(BriefDeliverableStatusEnum)
    .filter(
      (item) =>
        ![
          BriefDeliverableStatusEnum.Uploaded,
          BriefDeliverableStatusEnum.Approved,
          BriefDeliverableStatusEnum.PendingApproval,
        ].includes(item as BriefDeliverableStatusEnum)
    )
    .map((item) => ({
      label: item,
      value: item,
    })),
  {
    label: "Approval Pending",
    value: "Approval-Pending",
  },
];

interface DeliverableTabItemProps {
  item: IBriefDeliverableConfig;
  isSelected: boolean;
  onSelect: () => void;
  filters?: IBriefCampaignInfluencerSearchRequestV2;
  setFilters?: React.Dispatch<
    React.SetStateAction<IBriefCampaignInfluencerSearchRequestV2>
  >;
}

const DeliverableTabItem: React.FC<DeliverableTabItemProps> = ({
  item,
  isSelected,
  onSelect,
  filters,
  setFilters,
}) => {
  const [open, setOpen] = useState(false);

  const deliverableFilter =
    filters?.deliverables?.[item.deliverableConfigRefId]?.lastestTask;

  const filterCount =
    (deliverableFilter?.statuses?.length || 0) +
    ((deliverableFilter?.approvalPendingOn?.length || 0) > 0 ? 1 : 0);
  const isFiltered = filterCount > 0;

  const selectedValues = useMemo(() => {
    return [
      ...(deliverableFilter?.statuses || []),
      ...((deliverableFilter?.approvalPendingOn?.length || 0) > 0
        ? ["Approval-Pending"]
        : []),
    ];
  }, [deliverableFilter]);

  const handleApply = (value: string[]) => {
    const isApprovalPendingSelected = value.includes("Approval-Pending");
    const approvalItems = isApprovalPendingSelected
      ? Object.values(UploaderType)
      : [];
    const statusItems = value.filter((s) => s !== "Approval-Pending");

    if (setFilters) {
      setFilters((prev) => {
        const currentDeliv =
          prev.deliverables?.[item.deliverableConfigRefId] || {};
        const updated = { ...currentDeliv };

        if (statusItems.length === 0 && approvalItems.length === 0) {
          delete updated.lastestTask;
        } else {
          updated.lastestTask = {
            statuses: statusItems as BriefDeliverableStatusEnum[],
            approvalPendingOn: approvalItems as UploaderType[],
          };
        }

        return {
          ...prev,
          deliverables: {
            [item.deliverableConfigRefId]: updated,
          },
        };
      });
    }
    setOpen(false);
  };

  const handleReset = () => {
    if (setFilters) {
      setFilters((prev) => {
        const currentDeliv = prev.deliverables?.[item.deliverableConfigRefId];
        if (!currentDeliv) return prev;

        const updated = { ...currentDeliv };
        delete updated.lastestTask;

        return {
          ...prev,
          deliverables: {
            [item.deliverableConfigRefId]: updated,
          },
        };
      });
    }
    setOpen(false);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onSelect();
        }
      }}
      className={classNames(styles.deliverableTabBtn, {
        [styles.deliverableTabBtnActive]: isSelected,
      })}
    >
      <span className={styles.tabName}>{item.name}</span>
      <Popover
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
        arrow={false}
        destroyTooltipOnHide
        zIndex={1050}
        overlayClassName={styles.filterPopoverOverlay}
        content={
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <SearchMultiSelectFilter
              title={`${item.name} Status`}
              confirm={() => setOpen(false)}
              close={() => setOpen(false)}
              searchPlaceholder="Search Status"
              filterCount={filterCount}
              options={DELIVERABLE_STATUS_OPTIONS}
              selectedValues={selectedValues}
              onApply={handleApply}
              onReset={handleReset}
            />
          </div>
        }
      >
        <span
          role="button"
          tabIndex={0}
          className={classNames(styles.tabFilterTrigger, {
            [styles.tabFilterTriggerActive]: isFiltered || open,
            [styles.tabFilterTriggerOnSelected]: isSelected,
          })}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              setOpen((prev) => !prev);
            }
          }}
          title={`Filter ${item.name} Status`}
        >
          <Filter size={11} className={styles.filterIcon} />
          {filterCount > 0 && (
            <span className={styles.filterCountBadge}>{filterCount}</span>
          )}
        </span>
      </Popover>
    </div>
  );
};

export const CampaignExecutionDeliverableTabs: React.FC<
  CampaignExecutionDeliverableTabsProps
> = ({
  config,
  selectedDeliverableId,
  onSelectDeliverable,
  filters,
  setFilters,
  isAllExpanded,
  onToggleExpandAll,
}) => {
  if (!config || config.length === 0) return null;

  return (
    <div className={styles.deliverableTabsBar}>
      <span className={styles.deliverableTabsLabel}>Show:</span>
      <div className={styles.deliverableTabsList}>
        <button
          type="button"
          onClick={() => onSelectDeliverable(null)}
          className={classNames(styles.deliverableTabBtn, {
            [styles.deliverableTabBtnActive]: !selectedDeliverableId,
          })}
        >
          <span>All Deliverables</span>
        </button>
        {config.map((item) => {
          const isSelected =
            selectedDeliverableId === item.deliverableConfigRefId;

          return (
            <DeliverableTabItem
              key={item.deliverableConfigRefId}
              item={item}
              isSelected={isSelected}
              onSelect={() =>
                onSelectDeliverable(
                  isSelected ? null : item.deliverableConfigRefId
                )
              }
              filters={filters}
              setFilters={setFilters}
            />
          );
        })}
      </div>
      {onToggleExpandAll && (
        <div className={styles.expandAllWrapper}>
          <button
            type="button"
            onClick={onToggleExpandAll}
            className={styles.expandAllBtn}
          >
            {isAllExpanded ? (
              <ChevronDown size={14} className={styles.expandAllIcon} />
            ) : (
              <ChevronRight size={14} className={styles.expandAllIcon} />
            )}
            <span>{isAllExpanded ? "Collapse All" : "Expand All"}</span>
          </button>
        </div>
      )}
    </div>
  );
};
